import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import * as Yup from "yup";
import { useIntl } from "react-intl";

import basicAuthRequest from "axios/basicAuthRequest";
import {
  ZIPCODE_CODE,
  INVOICE,
  INVOICE_AUTOMATIC,
  INVOICE_AUTOMATIC_USER,
  INVOICE_FISCAL_REGIME_CATALOG,
} from "../../../../constants/api";

import { ZIP_CODE_LENGTH } from "../../../../constants";
import translate from "../../../../i18n/translate";
import { validations } from "utils";

const DEFAULT_TOWN_VALUE = "none";
const FISCAL_REGIME_DEFAULT = { id: -1, disabled: true };

const initialValuesFactory = (values, options) => ({
  name: values?.name || values?.nombre || "",
  rfc: values?.rfc || "",
  fiscal_regime:
    (options &&
      options.find(
        (fiscalRegimeOption) => fiscalRegimeOption.id === values.idFiscalRegime
      )) ||
    FISCAL_REGIME_DEFAULT,
  address: values?.address || values?.calle || "",
  number: values?.number || values?.exterior || "",
  number_int: values?.number_int || values?.interior || "",
  zip_code: values?.zip_code || values?.cp || "",
  town: values?.town || values?.colonia || DEFAULT_TOWN_VALUE,
});

const makeZipCodeRequest = async (code) => {
  try {
    const response = await basicAuthRequest(
      ZIPCODE_CODE.replace("{code}", code)
    );

    return response;
  } catch (error) {
    return error;
  }
};

const makeBillingGetRequest = async () => {
  try {
    const response = await axios.get(INVOICE_AUTOMATIC_USER);

    return response;
  } catch (error) {
    return error;
  }
};

const makeBillingSaveRequest = async (values, isAutomaticBilling) => {
  const url = isAutomaticBilling ? INVOICE_AUTOMATIC : INVOICE;
  try {
    const response = await axios.post(url, {
      ...values,
      fiscal_regime: values.fiscal_regime.id,
    });
    toast.success(
      isAutomaticBilling
        ? translate("invoices.updateSuccess")
        : translate("invoices.createSuccess")
    );

    return response;
  } catch (error) {
    return error.response;
  }
};

const validationSchemaFactory = (towns) =>
  Yup.object().shape({
    name: Yup.string().required(translate("common.requiredField")),
    rfc: Yup.string()
      .required(translate("common.requiredField"))
      .min(12, translate("common.fieldLengthMustBeAtLeast", { length: 12 }))
      .max(13, translate("common.fieldLengthMustBeBellow", { length: 14 })),
    fiscal_regime: Yup.object()
      .test(
        "fiscalRegimeValid",
        translate("common.requiredField"),
        (options) => {
          return options.id !== FISCAL_REGIME_DEFAULT.id;
        }
      )
      .shape({
        name: Yup.string().required(),
        id: Yup.number().required(),
      })

      .required(translate("common.requiredField")),
    address: Yup.string().required(translate("common.requiredField")),
    number: Yup.string().required(translate("common.requiredField")),
    number_int: Yup.string(),
    zip_code: validations.zipCode
      .test(
        "zipCodeValid",
        translate("common.invalidZipCode"),
        () => !!towns.length
      )
      .required(translate("common.requiredField")),
    town: Yup.mixed()
      .oneOf(towns, translate("common.requiredField"))
      .required(translate("common.requiredField")),
  });

const useBilling = (getInvoices) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [loadingTowns, setLoadingTowns] = useState(false);
  const [towns, setTowns] = useState([]);
  const [initialValues, setInitialValues] = useState();
  const [validationSchema, setValidationSchema] = useState();
  const [showManualBilling, setShowManualBilling] = useState(false);
  const [automaticBilling, setAutomaticBilling] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [fiscalRegimeOptions, setFiscalRegimeOptions] = useState();
  const intl = useIntl();

  const onBillingClick = (isAutomaticBilling = false) => {
    setShowBilling(true);
    setAutomaticBilling(isAutomaticBilling);
  };

  const onSubmit = (values) => {
    setLoading(true);
    setError();
    const postBilling = async () => {
      const response = await makeBillingSaveRequest(values, automaticBilling);

      if (response?.data?.success) {
        const data = response.data.result;
        setInitialValues(initialValuesFactory(data, fiscalRegimeOptions));
        getInvoices();
        setShowBilling(false);
      } else {
        setError(
          response?.data?.error?.description ||
            intl.formatMessage({ id: "errors.somethingWrong" })
        );
      }

      setLoading(false);
    };

    postBilling();
  };

  const onZipCodeChange = async (zipCode) => {
    if (zipCode && zipCode.length === ZIP_CODE_LENGTH) {
      await setTownsByZipCode(zipCode);
    }
  };

  const setTownsByZipCode = async (selectedZipCode) => {
    setLoadingTowns(true);
    const response = await makeZipCodeRequest(selectedZipCode);

    if (response?.data?.success) {
      setTowns(response?.data?.result || []);
      const townNames = response?.data?.result.map((item) => item.town);
      setValidationSchema(validationSchemaFactory(townNames));
      setLoadingTowns(false);
      return true;
    }

    setTowns([]);
    setLoadingTowns(false);
    return false;
  };

  useEffect(() => {
    const abortController = new AbortController();

    setLoading(true);
    const getBilling = async (options) => {
      const response = await makeBillingGetRequest();
      if (response?.data?.success) {
        const data = response.data.result;
        const backendTown = data?.colonia ? [{ town: data.colonia }] : [];
        setTowns(backendTown);
        setInitialValues(initialValuesFactory(data, options));
        setValidationSchema(validationSchemaFactory([data?.colonia]));
        setShowManualBilling(!data || data.status === 0);
      } else {
        // TODO Add New Relic integration
        console.error("Backend error", response);
      }

      setLoading(false);
    };

    const getFiscalRegime = async () => {
      try {
        const result = await axios.get(INVOICE_FISCAL_REGIME_CATALOG);

        FISCAL_REGIME_DEFAULT.name = intl.formatMessage({
          id: "common.entry.fiscalRegime",
        });
        const fiscalRegimeOptionsWithDefault = [
          FISCAL_REGIME_DEFAULT,
          ...result.data.result,
        ];

        setFiscalRegimeOptions(fiscalRegimeOptionsWithDefault);
        getBilling(fiscalRegimeOptionsWithDefault);
      } catch (error) {
        console.error("Backend error", error);
      }
    };

    getFiscalRegime();

    return () => abortController.abort();
  }, [intl]);

  return {
    automaticBilling,
    fiscalRegimeOptions,
    error,
    setError,
    initialValues,
    loading,
    loadingTowns,
    onBillingClick,
    onSubmit,
    onZipCodeChange,
    setShowBilling,
    showBilling,
    showManualBilling,
    towns,
    validationSchema,
  };
};

export default useBilling;
